import { Button, Divider, TextField } from '@mui/material';
import DataLossWarningModal from 'components/DataLossWarningModal';
import Spacing from 'components/Spacing';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import VehicleSelector from 'components/new/VehicleSelector';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import { investment as rules } from 'further-ui/validations';
import { FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAsset } from '../hooks/useAsset';
import useHoldingClasses from '../hooks/useHoldingClasses';
import AssetDetailsForm from './AssetForm/AssetDetailsForm';
import AssetSelection from './AssetForm/AssetSelection';
import HoldingClassDetailsForm from './HoldingClassForm';

type InputDetailsFormProps = {
  selectedVehicle: Vehicle;
};

const InputDetailsForm: FC<InputDetailsFormProps> = ({ selectedVehicle }) => {
  const { control, watch, setValue, getValues, setError } =
    useFormContext<rules.CreateInvestmentFormValues>();

  const removeModalDisclosure = useDisclosure();
  const [previousAssetId, setPreviousAssetId] = useState<string>('');

  const { selectedAssetId, isNewAsset, assetOptions, resetAssetDropdown } =
    useAsset({
      control,
      watch,
      setValue,
      assetCurrency: selectedVehicle.baseCurrency,
      vehicleId: selectedVehicle._id,
    });

  const {
    fields,
    addHoldingClass,
    removeHoldingClass,
    getAvailableOptions,
    isNewHoldingClass,
    resetHoldingClassDropdown,
    resetHoldingClassList,
    isHoldingClassSelectDisabled,
    hasNewHoldingClass,
  } = useHoldingClasses(
    {
      control,
      watch,
      setValue,
      getValues,
      setError,
    },
    selectedAssetId,
    isNewAsset,
  );

  const handleAssetChange = async (
    name: string,
    selectedAssetId?: string,
    baseCurrency?: CurrencyCode,
  ) => {
    if (!selectedAssetId || !baseCurrency) {
      setValue('holdingClasses.0.isNewHoldingClass', true);
      setValue('asset.name', '');
      setValue('asset.baseCurrency', selectedVehicle.baseCurrency);
      return;
    }

    setValue('asset.name', name);
    setValue('asset.baseCurrency', baseCurrency);

    if (hasNewHoldingClass()) {
      setPreviousAssetId(selectedAssetId);

      removeModalDisclosure.onOpen();
    } else {
      resetHoldingClassList();
    }
  };

  const handleConfirmAssetChange = () => {
    resetHoldingClassList();
    removeModalDisclosure.onClose();
  };

  const handleCancelAssetChange = () => {
    setValue('assetId', previousAssetId);
    setValue('isNewAsset', false);
    removeModalDisclosure.onClose();
  };

  return (
    <Spacing>
      <FormField label="Vehicle" size="md">
        <VehicleSelector selectedVehicle={selectedVehicle} />
      </FormField>

      <AssetSelection
        isNewAsset={isNewAsset}
        assetOptions={assetOptions}
        control={control}
        resetAssetDropdown={resetAssetDropdown}
        onAssetChange={handleAssetChange}
      />
      {isNewAsset && <AssetDetailsForm />}

      <Divider />

      {fields.map((field, index) => (
        <HoldingClassDetailsForm
          key={field.id}
          index={index}
          field={field}
          holdingClassOptions={getAvailableOptions(index)}
          onRemove={() => removeHoldingClass(index)}
          isFirst={index === 0}
          resetHoldingClassDropdown={(index) =>
            resetHoldingClassDropdown(index)
          }
          isNewHoldingClass={isNewHoldingClass(index)}
          isHoldingClassSelectDisabled={isHoldingClassSelectDisabled(index)}
          vehicleCurrencyCode={selectedVehicle.baseCurrency}
          assetBaseCurrency={getValues('asset.baseCurrency')}
        />
      ))}
      <Divider />
      <Row>
        <Button onClick={addHoldingClass} variant="outlined" size="large">
          Add additional holding classes to deal
        </Button>
      </Row>

      <Divider />
      <FormField label="Deal name*" size="md">
        <Controller
          name="dealName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Enter a deal name"
              size="small"
              fullWidth
            />
          )}
        />
      </FormField>

      <DataLossWarningModal
        disclosure={removeModalDisclosure}
        onConfirm={handleConfirmAssetChange}
        onCancel={handleCancelAssetChange}
        message="Changing the asset will remove all holding classes including any new ones you've created. Do you want to continue?"
      />
    </Spacing>
  );
};

export default InputDetailsForm;
